import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { MenuProps } from 'antd';
import { useAuth } from '@/hooks/useAuth';
import { useRbac } from '@/hooks/useRbac';
import { buildSidebarMenuItems, type SidebarMenuItems } from '@/components/Layout/Sidebar/menuItems';

type MenuItem = NonNullable<MenuProps['items']>[number];

export interface ActiveMenuChild {
  key: string;
  path: string;
  label: React.ReactNode;
}

export interface ActiveMenuGroup {
  /** Key of the parent group (e.g. 'finance-group' or a route path). */
  key: string;
  /** Parent display label (ReactNode — may include tier badge). */
  label: React.ReactNode;
  /** All sibling children of the active child (incl. the active one). */
  children: ActiveMenuChild[];
  /** The child that matches current pathname. */
  activeChild: ActiveMenuChild | null;
}

interface FlatChild {
  key: string;
  path: string;
  label: React.ReactNode;
  rootKey: string;
  parentKey: string;
  parentLabel: React.ReactNode;
  siblings: ActiveMenuChild[];
}

/**
 * Strip synthetic suffixes from menu keys before using them as routes.
 * Examples:
 * - `/inventory-root` -> `/inventory`
 * - `/accounting/purchase/documents__po` -> `/accounting/purchase/documents`
 */
function normalizeKey(key: string): string {
  const withoutScopedSuffix = key.split('__')[0];
  return withoutScopedSuffix.endsWith('-root')
    ? withoutScopedSuffix.slice(0, -'-root'.length)
    : withoutScopedSuffix;
}

function hasChildren(
  item: MenuItem,
): item is MenuItem & { children: NonNullable<SidebarMenuItems> } {
  return !!item && 'children' in item && Array.isArray((item as { children?: unknown }).children);
}

function flattenChildren(items: SidebarMenuItems): FlatChild[] {
  const flat: FlatChild[] = [];

  const walk = (nodes: SidebarMenuItems, rootKey?: string) => {
    (nodes ?? []).forEach((item) => {
      if (!item || !('key' in item)) return;
      if ((item as { type?: string }).type === 'divider') return;
      if (!hasChildren(item)) return;

      const parentKey = String(item.key);
      const currentRootKey = rootKey ?? parentKey;
      const parentLabel = (item as { label?: React.ReactNode }).label ?? parentKey;
      const childItems = item.children
        .filter((c): c is MenuItem & { key: React.Key } => !!c && 'key' in c)
        .filter((c) => (c as { type?: string }).type !== 'divider');

      const siblings: ActiveMenuChild[] = childItems
        .filter((child) => !hasChildren(child))
        .map((child) => ({
          key: String(child.key),
          path: normalizeKey(String(child.key)),
          label: (child as { label?: React.ReactNode }).label ?? String(child.key),
        }));

      siblings.forEach((sibling) => {
        flat.push({
          key: sibling.key,
          path: sibling.path,
          label: sibling.label,
          rootKey: currentRootKey,
          parentKey,
          parentLabel,
          siblings,
        });
      });

      walk(childItems, currentRootKey);
    });
  };

  walk(items);

  return flat;
}

/**
 * Find the menu entry whose `path` is the longest prefix of `pathname`.
 * Prefers exact match, then longest prefix, then null.
 */
function findByPath(flat: FlatChild[], pathname: string): FlatChild | null {
  let best: FlatChild | null = null;
  let bestScore = -1;
  const accountingBonus = pathname.startsWith('/accounting/') ? 10000 : 0;

  for (const entry of flat) {
    const isExact = entry.path === pathname;
    const isPrefix = entry.path !== '/' && pathname.startsWith(entry.path + '/');
    if (!isExact && !isPrefix) continue;

    const score =
      entry.path.length +
      (isExact ? 1000 : 0) +
      (entry.rootKey === 'accounting-group' ? accountingBonus : 0);

    if (score > bestScore) {
      best = entry;
      bestScore = score;
    }
  }

  return best;
}

/**
 * Returns the active menu group + siblings for the current route.
 * Used by SubNavBar to render the horizontal sibling strip at the top of
 * content area. Returns null when:
 *  - Route matches a top-level item without children (Dashboard, Customers, ...)
 *  - Route doesn't match any menu entry at all.
 */
export function useActiveMenuGroup(): ActiveMenuGroup | null {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission } = useRbac();
  const { user } = useAuth();

  const flat = useMemo(
    () =>
      flattenChildren(
        buildSidebarMenuItems({
          hasPermission,
          onNavigate: navigate,
          userRole: user?.role,
        }),
      ),
    [hasPermission, navigate, user?.role],
  );

  return useMemo(() => {
    const match = findByPath(flat, location.pathname);
    if (!match) return null;
    return {
      key: match.parentKey,
      label: match.parentLabel,
      children: match.siblings,
      activeChild: match.siblings.find((s) => s.key === match.key) ?? null,
    };
  }, [flat, location.pathname]);
}
